import find from 'lodash/find';
import { COMPONENT, PATHWAY } from 'services/coursewareItem/coursewareTypes';
import CoursewareItemProvider from '../coursewareItem/CoursewareItemProvider';
import client from '../../ConnectionManager/apolloClient';
import { getElementType as elementTypeQuery } from './queries/getElementType.gql';
import { getElementTypeAndAncestry as elementTypeAndAncestryQuery } from './queries/getElementTypeAndAncestry.gql';
const ACTIVITY = 'ACTIVITY';
const INTERACTIVE = 'INTERACTIVE';
class CoursewareTargetProvider {
  constructor() {
    this._savedTypes = {};
  }

  saveCoursewareType({ id, type, elementType, ancestry = [], components = [] }) {
    if (!type && !elementType) {
      return null;
    }

    if (!this._savedTypes[id]) {
      this._savedTypes[id] = {
        id,
        type: type || elementType,
        ancestry,
      };
    }

    // Always run saveCoursewareType recursively on any given components so the
    // function can be safely used repeatedly with different levels of detail.
    //
    // This occurs loading child Walkables (eg. screens); the first time we come
    // across a screen it is included as part of a parent Activity (eg. lesson), but
    // it doesn't have any components. The second time we come across the screen
    // we're loading it directly, which includes components, ready for caching.
    components.forEach(({ id: componentId }) => {
      this.saveCoursewareType({
        id: componentId,
        elementType: COMPONENT,
        ancestry: [
          {
            elementId: id,
            elementType: type || elementType,
          },
          ...this._savedTypes[id].ancestry,
        ],
      });
    });

    return this._savedTypes[id];
  }

  /**
   * The function returns the courseware type and ancestry
   * @param {string} elementId :: UUID of courseware element
   * @param {string} elementType :: type of courseware element
   * @returns {Object} type and ancestry of courseware element
   */
  async getCoursewareTypeAndAncestry(elementId, elementType) {
    if (this._savedTypes[elementId]) {
      return this._savedTypes[elementId];
    }

    const { cohortId, deploymentId } = CoursewareItemProvider.getDeploymentContext();

    const getElementTypeAndAncestry = async () => {
      try {
        const { data } = await client.query({
          query: elementTypeAndAncestryQuery,
          variables: {
            cohortId,
            deploymentId,
            elementId,
          },
        });
        const { elementId: id, ancestry, type } = data.learn.cohort.deployment[0].getLearnerAncestry;
        return this.saveCoursewareType({ id, type, ancestry });
      } catch (e) {
        return undefined;
      }
    };

    switch (elementType) {
      case PATHWAY:
        return {};
      default:
        return await getElementTypeAndAncestry() || {};
    }
  }

  async getWalkableType(walkableId) {
    if (this._savedTypes[walkableId]) {
      return this._savedTypes[walkableId];
    }

    const { cohortId, deploymentId } = CoursewareItemProvider.getDeploymentContext();
    const getElementType = async () => {
      try {
        const { data } = await client.query({
          query: elementTypeQuery,
          variables: {
            cohortId,
            deploymentId,
            elementId: walkableId,
          },
        });
        const { elementType } = data.learn.cohort.deployment[0].getLearnerElement;
        return { type: elementType };
      } catch (e) {
        return undefined;
      }
    };
    return getElementType();
  }

  async createCoursewareTarget(id, elementType) {
    const { cohortId, deploymentId } = CoursewareItemProvider.getDeploymentContext();
    const { type, ancestry } = await this.getCoursewareTypeAndAncestry(id, elementType);

    return {
      id,
      type,
      cohortId,
      deploymentId,
      ancestry,
    };
  }

  getImmediateParentId(ancestry = []) {
    if (!ancestry.length) return '';
    const parent = find(ancestry, (element) => element.elementType === INTERACTIVE || element.elementType === ACTIVITY);
    return parent?.elementId;
  }

  getImmediateParentType(ancestry = []) {
    if (!ancestry.length) return '';
    const parent = find(ancestry, (element) => element.elementType === INTERACTIVE || element.elementType === ACTIVITY);
    return parent?.elementType;
  }
}

export default new CoursewareTargetProvider();
